import IconsGrid from "components/Icons/IconcsGrid";
import SubscriptionPlans from "components/Subscription/Subscription";
import { OWNER_BENEFITS, OWNER_EVENTS_DATA, OWNER_FAQ, OWNER_HOW_IT_WORKS } from "data/constants";
import { motion, useAnimation, useInView } from "framer-motion";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CallToActionHeader from "./CallToActionHeader";
import CallToFaqSection from "./CallToFaqSection";
import HowItWorkCallToAction from "./HowItWorkCallToAction";
import OwnerCallToActionImage from "./OwnerCallToActionImage";
import { Helmet } from "react-helmet";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { FaRocket, FaCheck, FaStar, FaUsers, FaChartLine, FaHome } from "react-icons/fa";
import { EASING } from "../../utils/easingUtils";

const OwnerCallToAction = () => {
  const benefitsRef = useRef(null);
  const callToActionImageRef = useRef(null);

  const benefitsControls = useAnimation();
  const callToActionImageControls = useAnimation();

  const isBenefitsInView = useInView(benefitsRef, { once: true, margin: "-100px" });
  const isCallToActionImageInView = useInView(callToActionImageRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isBenefitsInView) {
      benefitsControls.start("visible");
    }
  }, [isBenefitsInView, benefitsControls]);

  useEffect(() => {
    if (isCallToActionImageInView) {
      callToActionImageControls.start("visible");
    }
  }, [isCallToActionImageInView, callToActionImageControls]);
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>
          Espaces événementiels Paris, Île-de-France et partout en France : trouvez le lieu idéal | Event To Bee{" "}
        </title>
        <meta
          name="description"
          content="Des espaces événementiels : salles de réception, jardins, lofts, studios, popup store, lieux atypiques. Des lieux pour tous vos événements privés et professionnels."
        />
      </Helmet>

      <div>
        <div className="bg-gradient-to-br from-gray-50 to-gray-200">
          <CallToActionHeader
            title="Attirez plus de réservations en publiant votre espace"
            highlightText="espace"
            highlightTextColor="bg-yellow-300"
            description="Rejoignez EventToBee et donnez de la visibilité à votre espace auprès de milliers de clients à la recherche du lieu parfait pour leur événement. "
            buttonText="Commencer"
            buttonAction={() => navigate("/add-location")}
            imageSrc="modern-illustration"
            imageWidth="w-96"
            imageHeight="w-96"
          />
        </div>
        {/* Benefits Section - Modernized */}
        <div
          className="relative overflow-hidden bg-gradient-to-br from-pink-50 via-white to-rose-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 px-4 py-20 mt-20"
          ref={benefitsRef}
        >
          {/* Background Effects */}
          <BgGlassmorphism />
          <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-pink-200/30 to-rose-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl"></div>

          <div className="container relative mx-auto">
            {/* Hero Section */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-full text-sm font-medium text-pink-600 mb-6">
                <FaHome className="w-4 h-4 mr-2 text-pink-500" />
                Propriétaires d'espaces
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white mb-6">
                Les bonnes raisons de référencer votre espace sur EventToBee
              </h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto leading-relaxed">
                Rejoignez notre plateforme et maximisez la rentabilité de votre espace événementiel
              </p>
            </motion.div>

            {/* Benefits Grid */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8 max-w-7xl mx-auto"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {OWNER_BENEFITS.map((value, index) => (
                <motion.div
                  key={value.id}
                  className="group relative bg-white dark:bg-neutral-800 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 border border-neutral-200 dark:border-neutral-700"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                >
                  {/* Icon Circle */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <img
                        src={value.icon}
                        alt={value.title}
                        width={40}
                        height={40}
                        className="filter brightness-0 invert"
                      />
                    </div>
                    {/* Decorative element */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-accent-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <FaCheck className="w-3 h-3 text-white m-1.5" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h5 className="text-xl font-bold text-neutral-900 dark:text-white mb-4 group-hover:text-pink-600 transition-colors duration-300">
                      {value.title}
                    </h5>
                    <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-6">
                      {value.description}
                    </p>

                    {/* Progress indicator */}
                    <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mb-4 mx-2">
                      <div
                        className="bg-gradient-to-r from-pink-500 to-rose-500 h-2 rounded-full transition-all duration-1000 delay-500"
                        style={{ width: `${Math.min((index + 1) * 16.66, 100)}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-center text-xs text-neutral-500 dark:text-neutral-400 px-2">
                      <FaChartLine className="w-3 h-3 mr-1" />
                      <span className="text-center">Impact revenus</span>
                    </div>
                  </div>

                  {/* Hover Border Effect */}
                  <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-pink-500/50 transition-colors duration-500"></div>
                </motion.div>
              ))}
            </motion.div>

            {/* Call to Action */}
            <motion.div
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.6 }}
            >
              <div className="bg-gradient-to-r from-pink-500 via-pink-600 to-rose-500 rounded-3xl p-8 text-white max-w-4xl mx-auto">
                <div className="flex items-center justify-center mb-4">
                  <FaStar className="w-6 h-6 mr-2" />
                  <span className="text-lg font-semibold">Prêt à référencer votre espace ?</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">Commencez dès aujourd'hui</h3>
                <p className="text-lg mb-6 opacity-90">
                  Publication gratuite et mise en ligne immédiate de votre espace
                </p>
                <button
                  className="bg-white text-pink-600 px-8 py-4 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg"
                  onClick={() => navigate("/add-location")}
                >
                  Ajouter mon espace
                </button>
              </div>
            </motion.div>
          </div>
        </div>
        <div className="mt-20">
          <HowItWorkCallToAction
            title={OWNER_HOW_IT_WORKS.title}
            stepCircleColor="bg-eventToBee"
            steps={OWNER_HOW_IT_WORKS.steps}
            titleHighlight=""
          />
        </div>
        <div className="mt-20 dark:bg-neutral-800">
          <IconsGrid
            events={OWNER_EVENTS_DATA}
            title="Chaque lieu est un EventToBee"
            description="Fêtes privées, événements d'entreprise, réunions, séances photo... choisissez vos réservations. Proposez votre espace à des milliers de clients à la recherche de lieux similaires"
          />
        </div>

        <div ref={callToActionImageRef} className="mt-20">
          <OwnerCallToActionImage
            controls={callToActionImageControls}
            title="Votre espace mérite d'être vu"
            description="Découvrez notre sélection d’espaces événementiels disponibles à Paris et en Île-de-France. Que vous cherchiez une salle de réception, un studio, un jardin ou même une péniche, vous trouverez ici le lieu parfait pour tous vos événements, privés ou professionnels"
          />
        </div>

        <SubscriptionPlans />
        <div className="mt-20">
          <CallToFaqSection toggleBgColor="bg-eventToBee" faqs={OWNER_FAQ} />
        </div>
      </div>
    </>
  );
};

export default OwnerCallToAction;
